import { Suspense } from "react";
import { DashboardBootPlate } from "./features/dashboard/DashboardBootPlate";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import Layout from "./components/Layout";
import AuthGuard from "./components/Auth/AuthGuard";
import ActivityLiveSubscriber from "./components/ActivityLiveSubscriber";
import SeoManager from "./components/SeoManager";
import { RouteScrollReset } from "./components/RouteScrollReset";
import { ToastProvider } from "./components/ui/Toast";
import PrivacyBanner from "./components/PrivacyBanner";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ThemeProvider } from "./hooks/useTheme";
import { InstantMotionRoot } from "./components/InstantMotionRoot";
import { useUserSession } from "./contexts/UserSessionContext";
import { ROUTES } from "./lib/routes";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import {
  PROTECTED_ROUTE_ERROR_DESCRIPTION,
  PROTECTED_ROUTE_ERROR_TITLE,
} from "./lib/protectedRouteErrorCopy";
import {
  BONK_LAB_UNAVAILABLE_BACK_LABEL,
  BONK_LAB_UNAVAILABLE_BODY,
  BONK_LAB_UNAVAILABLE_TITLE,
  FeatureDoorRoute,
  FeatureDoorUnavailable,
} from "./components/FeatureDoorRoute";
import "./App.css";

// ============================================
// Critical path — eagerly loaded (first paint)
// ============================================
import Landing from "./pages/Landing";
import AlpenbrevetCampaign from "./pages/AlpenbrevetCampaign";
import EroicaCampaign from "./pages/EroicaCampaign";
import Login from "./components/Auth/Login";
import Register from "./components/Auth/Register";
import OAuthCallback from "./components/Auth/OAuthCallback";
import { PublicMarketingLayout } from "./components/PublicMarketingLayout";
import RecoveryDrinkRedirect from "./pages/RecoveryDrinkRedirect";

// ============================================
// Lazy-loaded pages — split into separate chunks
// ============================================

// Auth flows
const EmailVerification = lazyWithRetry(() => import("./pages/EmailVerification"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const ForgotPassword = lazyWithRetry(() => import("./components/Auth/ForgotPassword"));

// Core app pages
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const TrainingPlans = lazyWithRetry(() => import("./pages/TrainingPlans"));
const TrainingPlan = lazyWithRetry(() => import("./pages/TrainingPlan"));
const PlanGenerator = lazyWithRetry(() => import("./pages/PlanGenerator"));
const RaceDayReady = lazyWithRetry(() => import("./pages/RaceDayReady"));
const Coach = lazyWithRetry(() => import("./pages/Coach"));
const Nutrition = lazyWithRetry(() => import("./pages/Nutrition"));
const QuickFill = lazyWithRetry(() => import("./pages/QuickFill"));
const Activities = lazyWithRetry(() => import("./pages/Activities"));
const PersonalBests = lazyWithRetry(() => import("./pages/PersonalBests"));
const RoutesHub = lazyWithRetry(() => import("./pages/Routes"));
const RouteBriefing = lazyWithRetry(() => import("./pages/RouteBriefing"));
const ActivityDetail = lazyWithRetry(() => import("./pages/ActivityDetail"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const FuelingHistory = lazyWithRetry(() => import("./pages/FuelingHistory"));
const BonkLab = lazyWithRetry(() => import("./pages/BonkLab"));
const BonkHistory = lazyWithRetry(() => import("./pages/BonkHistory"));
const Progress = lazyWithRetry(() => import("./pages/Progress"));
const Rewards = lazyWithRetry(() => import("./pages/Rewards"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));

// Beta features
const MealPlan = lazyWithRetry(() => import("./pages/MealPlan"));
const DIYNutrition = lazyWithRetry(() => import("./pages/DIYNutrition"));
const FuelPlannerV2 = lazyWithRetry(() => import("./pages/FuelPlannerV2"));
const FuelPlanExecutionReview = lazyWithRetry(() =>
  import("./pages/FuelPlanExecutionReview")
);
const Mobility = lazyWithRetry(() => import("./pages/Mobility"));
const Recovery = lazyWithRetry(() => import("./pages/Recovery"));
const BikeFit = lazyWithRetry(() => import("./pages/BikeFit"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const TrainerSession = lazyWithRetry(() => import("./pages/TrainerSession"));

// Admin
const AdminDashboard = lazyWithRetry(() => import("./pages/Admin/AdminDashboard"));
const AdminUsers = lazyWithRetry(() => import("./pages/Admin/AdminUsers"));
const AdminPromoCodes = lazyWithRetry(() => import("./pages/Admin/AdminPromoCodes"));
const AdminAuditLog = lazyWithRetry(() => import("./pages/Admin/AdminAuditLog"));
const AdminAlerts = lazyWithRetry(() => import("./pages/Admin/AdminAlerts"));
const AdminDeps = lazyWithRetry(() => import("./pages/Admin/AdminDeps"));
const AdminReferralsPromos = lazyWithRetry(() =>
  import("./pages/Admin/AdminReferralsPromos")
);

// Legal / Marketing
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const LegalPrivacy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const About = lazyWithRetry(() => import("./pages/About"));
const IntervalsSetup = lazyWithRetry(() => import("./pages/IntervalsSetup"));
const Install = lazyWithRetry(() => import("./pages/Install"));
const AndroidBeta = lazyWithRetry(() => import("./pages/AndroidBeta"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const DesignSystem = lazyWithRetry(() => import("./pages/DesignSystem"));

// ============================================
// Loading fallback
// ============================================
function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <div className="flex flex-col items-center gap-3">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--border-subtle)] border-t-primary-600"
          aria-hidden
        />
        <p className="text-sm text-[var(--text-muted)]">Loading…</p>
      </div>
    </div>
  );
}

/** Wrap a lazy component with Suspense */
function LazyPage({ children }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/** Home chunk: same wordmark as the in-dashboard hold, not the route spinner. */
function DashboardRoute() {
  return (
    <Suspense fallback={<DashboardBootPlate />}>
      <Dashboard />
    </Suspense>
  );
}

/** Wrap a lazy component inside Layout + AuthGuard */
function ProtectedPage() {
  return (
    <AuthGuard>
      <ActivityLiveSubscriber />
      <Layout>
        <RouteErrorBoundary
          title={PROTECTED_ROUTE_ERROR_TITLE}
          description={PROTECTED_ROUTE_ERROR_DESCRIPTION}
        >
          <LazyPage>
            <Outlet />
          </LazyPage>
        </RouteErrorBoundary>
      </Layout>
    </AuthGuard>
  );
}

/** Admin routes — role comes from server-verified session (AuthGuard /me), not localStorage. */
function AdminPage() {
  return (
    <AuthGuard>
      <AdminRoleGate>
        <Layout>
          <LazyPage>
            <Outlet />
          </LazyPage>
        </Layout>
      </AdminRoleGate>
    </AuthGuard>
  );
}

function AdminRoleGate({ children }) {
  const session = useUserSession();
  const user = session?.user;

  if (user == null) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label="Loading" />
      </div>
    );
  }

  const isAdmin = user.role === "ADMIN" || user.role === "SUPERADMIN";
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  return (
    <ThemeProvider>
    <InstantMotionRoot>
    <ToastProvider>
      <div className="App">
        <SeoManager />
        <RouteScrollReset />
        <Routes>
          {/* Public Routes — Landing, Login, Register share persistent chrome */}
          <Route element={<PublicMarketingLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/about"
              element={
                <LazyPage>
                  <About />
                </LazyPage>
              }
            />
            <Route
              path="/intervals"
              element={
                <LazyPage>
                  <IntervalsSetup />
                </LazyPage>
              }
            />
            <Route
              path="/install"
              element={
                <LazyPage>
                  <Install />
                </LazyPage>
              }
            />
            <Route
              path="/beta"
              element={
                <LazyPage>
                  <AndroidBeta />
                </LazyPage>
              }
            />
            <Route
              path="/contact"
              element={
                <LazyPage>
                  <Contact />
                </LazyPage>
              }
            />
            <Route
              path="/terms"
              element={
                <LazyPage>
                  <TermsOfService />
                </LazyPage>
              }
            />
            <Route
              path="/privacy"
              element={
                <LazyPage>
                  <LegalPrivacy />
                </LazyPage>
              }
            />
          </Route>
          <Route path="/eroica" element={<EroicaCampaign />} />
          <Route path="/alpenbrevet" element={<AlpenbrevetCampaign />} />
          <Route path="/auth/oauth/callback" element={<OAuthCallback />} />
          <Route
            path={ROUTES.VERIFY_EMAIL}
            element={
              <LazyPage>
                <EmailVerification />
              </LazyPage>
            }
          />
          {/* Universal Link route for mobile app */}
          <Route
            path={ROUTES.APP_VERIFY_EMAIL}
            element={
              <LazyPage>
                <EmailVerification />
              </LazyPage>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <LazyPage>
                <ForgotPassword />
              </LazyPage>
            }
          />
          <Route
            path="/reset-password/:token"
            element={
              <LazyPage>
                <ResetPassword />
              </LazyPage>
            }
          />
          <Route
            path="/pricing"
            element={
              <LazyPage>
                <Pricing />
              </LazyPage>
            }
          />
          <Route path="/Intervals" element={<Navigate to="/intervals" replace />} />

          {/* Protected Routes — all lazy-loaded */}
          <Route
            path="/onboarding"
            element={
              <AuthGuard>
                <LazyPage>
                  <Onboarding />
                </LazyPage>
              </AuthGuard>
            }
          />
          <Route element={<ProtectedPage />}>
            <Route path="/dashboard" element={<DashboardRoute />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/training-plans" element={<TrainingPlans />} />
            <Route path="/training-plans/:id" element={<TrainingPlan />} />
            <Route path="/plan-generator" element={<PlanGenerator />} />
            <Route path="/race-day-ready" element={<RaceDayReady />} />
            <Route path="/coach" element={<FeatureDoorRoute door="coachChat"><Coach /></FeatureDoorRoute>} />
            <Route path="/nutrition" element={<Nutrition />} />
            <Route path="/nutrition/quick-fill" element={<QuickFill />} />
            <Route path="/meal-plan" element={<MealPlan />} />
            <Route path="/meal-plan/:planId" element={<MealPlan />} />
            <Route path="/diy-nutrition" element={<FeatureDoorRoute door="diyNutrition"><DIYNutrition /></FeatureDoorRoute>} />
            <Route path="/fuel-planner/review/:sessionId" element={<FuelPlanExecutionReview />} />
            <Route path="/fuel-planner/:sessionId" element={<FuelPlannerV2 />} />
            <Route path="/mobility" element={<FeatureDoorRoute door="mobilityLibrary"><Mobility /></FeatureDoorRoute>} />
            <Route path="/recovery" element={<Recovery />} />
            <Route path="/recovery-drink" element={<RecoveryDrinkRedirect />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/rewards" element={<Rewards />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/design-system" element={<DesignSystem />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/personal-bests" element={<FeatureDoorRoute door="records"><PersonalBests /></FeatureDoorRoute>} />
            <Route path="/activities/:id" element={<ActivityDetail />} />
            <Route path="/routes" element={<FeatureDoorRoute door="routes"><RoutesHub /></FeatureDoorRoute>} />
            <Route path="/routes/:id" element={<FeatureDoorRoute door="routes"><RouteBriefing /></FeatureDoorRoute>} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/fueling-history" element={<FuelingHistory />} />
            <Route path="/bonk-history" element={<BonkHistory />} />
            <Route
              path="/bonk-lab"
              element={
                <FeatureDoorRoute
                  door="bonkLab"
                  closedFallback={
                    <FeatureDoorUnavailable
                      testId="bonk-lab-unavailable"
                      title={BONK_LAB_UNAVAILABLE_TITLE}
                      body={BONK_LAB_UNAVAILABLE_BODY}
                      backTo="/bonk-history"
                      backLabel={BONK_LAB_UNAVAILABLE_BACK_LABEL}
                    />
                  }
                >
                  <BonkLab />
                </FeatureDoorRoute>
              }
            />
            <Route path="/bike-fit" element={<FeatureDoorRoute door="bikeFit"><BikeFit /></FeatureDoorRoute>} />
            <Route path="/trainer" element={<FeatureDoorRoute door="trainer"><TrainerSession /></FeatureDoorRoute>} />
            <Route path="/trainer/:sessionId" element={<FeatureDoorRoute door="trainer"><TrainerSession /></FeatureDoorRoute>} />
          </Route>

          {/* Admin Routes -- guarded by role check, non-admins redirected */}
          <Route element={<AdminPage />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
            <Route path="/admin/audit-log" element={<AdminAuditLog />} />
            <Route path="/admin/alerts" element={<AdminAlerts />} />
            <Route path="/admin/deps" element={<AdminDeps />} />
            <Route
              path="/admin/referrals-promos"
              element={<AdminReferralsPromos />}
            />
          </Route>
          <Route
            path="*"
            element={
              <LazyPage>
                <NotFound />
              </LazyPage>
            }
          />
        </Routes>
        <PrivacyBanner />
      </div>
    </ToastProvider>
    </InstantMotionRoot>
    </ThemeProvider>
  );
}

export default App;
